import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import StatusBadge from '../../components/shared/StatusBadge/StatusBadge';
import PromoterSalesView from '../../components/promoters/PromoterSalesView';
import PromoterCutsView from '../../components/promoters/PromoterCutsView';
import { PanelTabs, StatsGrid } from '../../components/promoters/promoters.styles';
import { Panel } from '../sales/Sales.styles';
import EmpresasTab from './EmpresasTab';
import ActivitiesTab from './ActivitiesTab';
import {
  PromoterSessionError,
  PromoterStatusError,
  getPromoterCut,
  listPromoterCuts,
  listPromoterLinks,
  listPromoterSales,
} from '../../services/promoterApi';
import { formatPercent } from '../../utils/commission';

const TABS = [
  { id: 'empresas', label: 'Empresas' },
  { id: 'actividades', label: 'Mis actividades' },
  { id: 'ventas', label: 'Ventas' },
  { id: 'cortes', label: 'Cortes' },
];

// Panel del promotor aprobado. Sirve igual al promotor persona (token propio)
// y a la empresa con perfil de promotor (su token de empresa).
//   mode 'persona' | 'empresa'; `me` = { promoter, links, canOperate }.
//   onSessionError(err): cierra la sesión del promotor persona.
//   onRefreshMe(): vuelve a pedir /api/promoter/me (estado y contadores).
const PromoterPanel = ({ mode, me, onSessionError, onRefreshMe }) => {
  const { promoter } = me;
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'empresas';

  const [links, setLinks] = useState({ loading: true, items: [], error: '' });

  const sessionRef = useRef(onSessionError);
  const refreshRef = useRef(onRefreshMe);
  sessionRef.current = onSessionError;
  refreshRef.current = onRefreshMe;

  // Errores comunes de todas las pestañas. true = ya se manejó (no mostrar mensaje).
  const handleError = useCallback((err) => {
    if (err instanceof PromoterSessionError) {
      if (sessionRef.current) sessionRef.current(err);
      return true;
    }
    if (err instanceof PromoterStatusError || err.code === 'not_a_promoter') {
      // El estado cambió (suspendido, etc.): se vuelve a leer y el panel muestra el nuevo estado.
      if (refreshRef.current) refreshRef.current();
      return true;
    }
    return false;
  }, []);

  const reloadLinks = useCallback(async () => {
    try {
      const data = await listPromoterLinks(mode, 'all');
      setLinks({ loading: false, items: data.links || [], error: '' });
    } catch (err) {
      if (handleError(err)) return;
      setLinks((current) => ({ ...current, loading: false, error: err.message }));
    }
  }, [mode, handleError]);

  useEffect(() => { reloadLinks(); }, [reloadLinks]);

  const acceptedEmpresas = useMemo(
    () => links.items
      .filter((l) => l.status === 'accepted')
      .map((l) => ({ id: l.empresa.id, name: l.empresa.businessName })),
    [links.items]
  );

  // Empresas con las que alguna vez hubo relación (para filtrar ventas y cortes).
  const knownEmpresas = useMemo(() => {
    const seen = new Map();
    links.items.forEach((l) => {
      if (l.status !== 'pending' && l.status !== 'rejected') seen.set(l.empresa.id, l.empresa.businessName);
    });
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [links.items]);

  const setTab = (id) => setParams({ tab: id }, { replace: true });

  const loadSales = useCallback((p) => listPromoterSales(mode, p), [mode]);
  const loadCuts = useCallback((p) => listPromoterCuts(mode, p), [mode]);
  const loadCut = useCallback((id) => getPromoterCut(mode, id), [mode]);

  const counts = me.links || {};

  return (
    <>
      <Panel>
        <h2>Tu cuenta de promotor</h2>
        <StatsGrid>
          <div className="stat">
            <div className="value" style={{ fontSize: 'var(--text-xl)' }}>{promoter.referralCode || '—'}</div>
            <div className="label">Tu código</div>
          </div>
          <div className="stat">
            <div className="value">{promoter.commissionPercent ? formatPercent(promoter.commissionPercent) : '—'}</div>
            <div className="label">Tu comisión</div>
          </div>
          <div className="stat">
            <div className="value">{counts.accepted || 0}</div>
            <div className="label">{counts.accepted === 1 ? 'Empresa aceptada' : 'Empresas aceptadas'}</div>
          </div>
          <div className="stat">
            <div className="value">{counts.pending || 0}</div>
            <div className="label">{counts.pending === 1 ? 'Solicitud pendiente' : 'Solicitudes pendientes'}</div>
          </div>
        </StatsGrid>
        <p style={{ marginTop: 12 }}>
          <StatusBadge status="approved">Aprobado</StatusBadge>
        </p>
      </Panel>

      <PanelTabs role="tablist" aria-label="Secciones del panel de promotor">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`ptab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls="ptab-panel"
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </PanelTabs>

      <div id="ptab-panel" role="tabpanel" aria-labelledby={`ptab-${tab}`}>
        {tab === 'empresas' && (
          <EmpresasTab
            mode={mode}
            links={links}
            onReloadLinks={reloadLinks}
            onChanged={() => refreshRef.current && refreshRef.current()}
            onError={handleError}
          />
        )}
        {tab === 'actividades' && (
          <ActivitiesTab
            mode={mode}
            promoter={promoter}
            acceptedEmpresas={acceptedEmpresas}
            linksLoaded={!links.loading && !links.error}
            onGoToEmpresas={() => setTab('empresas')}
            onError={handleError}
          />
        )}
        {tab === 'ventas' && (
          <PromoterSalesView
            viewer="promoter"
            load={loadSales}
            onError={handleError}
            empresaOptions={knownEmpresas}
          />
        )}
        {tab === 'cortes' && (
          <PromoterCutsView
            viewer="promoter"
            load={loadCuts}
            loadDetail={loadCut}
            onError={handleError}
            empresaOptions={knownEmpresas}
          />
        )}
      </div>
    </>
  );
};

export default PromoterPanel;
